import { Controller } from "@hotwired/stimulus"

const MAX_INTEGER_DIGITS = 10
const MAX_DECIMAL_DIGITS = 2

export default class extends Controller {
  connect() {
    this.integerDigits = ""
    this.decimalDigits = ""
    this.decimalMode = false

    this.setupHiddenInput()

    const initial = this.element.value?.trim()
    if (initial) {
      const parsed = this.parseDecimal(initial)
      if (parsed != null && parsed > 0) this.loadFromAmount(parsed)
    }

    this.boundKeydown = this.onKeydown.bind(this)
    this.boundPaste = this.onPaste.bind(this)

    this.element.addEventListener("keydown", this.boundKeydown)
    this.element.addEventListener("paste", this.boundPaste)

    this.render()
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.boundKeydown)
    this.element.removeEventListener("paste", this.boundPaste)
    this.hiddenInput?.remove()
  }

  setupHiddenInput() {
    const name = this.element.getAttribute("name")
    if (!name) return

    this.hiddenInput = document.createElement("input")
    this.hiddenInput.type = "hidden"
    this.hiddenInput.name = name
    this.element.removeAttribute("name")
    this.element.insertAdjacentElement("afterend", this.hiddenInput)
  }

  onKeydown(e) {
    if (e.ctrlKey || e.metaKey) return

    const navigation = ["Tab", "ArrowLeft", "ArrowRight", "Home", "End"]
    if (navigation.includes(e.key)) return

    if (e.key === "Backspace") {
      e.preventDefault()
      this.backspace()
      this.render()
      return
    }

    if (e.key === "Delete") {
      e.preventDefault()
      this.clear()
      this.render()
      return
    }

    if (e.key === "," || e.key === ".") {
      e.preventDefault()
      if (this.integerDigits !== "" && !this.decimalMode) {
        this.decimalMode = true
      }
      return
    }

    if (e.key >= "0" && e.key <= "9") {
      e.preventDefault()
      this.appendDigit(e.key)
      this.render()
      return
    }

    e.preventDefault()
  }

  onPaste(e) {
    e.preventDefault()

    const text = (e.clipboardData || window.clipboardData).getData("text")
    const parsed = this.parseDecimal(text)
    if (parsed == null || parsed <= 0) return

    this.loadFromAmount(parsed)
    this.render()
  }

  appendDigit(digit) {
    if (this.decimalMode) {
      if (this.decimalDigits.length >= MAX_DECIMAL_DIGITS) return
      this.decimalDigits += digit
      return
    }

    if (this.integerDigits.length >= MAX_INTEGER_DIGITS) return
    this.integerDigits += digit
    if (this.integerDigits.length > 1) {
      this.integerDigits = this.integerDigits.replace(/^0+/, "") || "0"
    }
  }

  backspace() {
    if (this.decimalMode && this.decimalDigits.length > 0) {
      this.decimalDigits = this.decimalDigits.slice(0, -1)
      return
    }

    if (this.decimalMode) {
      this.decimalMode = false
      return
    }

    this.integerDigits = this.integerDigits.slice(0, -1)
  }

  clear() {
    this.integerDigits = ""
    this.decimalDigits = ""
    this.decimalMode = false
  }

  loadFromAmount(amount) {
    const [intPart, decPart] = amount.toFixed(2).split(".")
    this.integerDigits = String(parseInt(intPart, 10))
    if (decPart === "00") {
      this.decimalMode = false
      this.decimalDigits = ""
    } else {
      this.decimalMode = true
      this.decimalDigits = decPart.replace(/0+$/, "") || decPart
    }
  }

  currentAmount() {
    if (this.integerDigits === "" && this.decimalDigits === "") return null

    const intPart = this.integerDigits === "" ? 0 : parseInt(this.integerDigits, 10)
    if (!this.decimalMode || this.decimalDigits === "") return intPart

    const dec = parseInt(this.decimalDigits.padEnd(2, "0"), 10)
    return intPart + dec / 100
  }

  render() {
    const amount = this.currentAmount()

    if (amount == null) {
      this.element.value = ""
      if (this.hiddenInput) this.hiddenInput.value = ""
    } else {
      this.element.value = this.formatDisplay(amount)
      if (this.hiddenInput) this.hiddenInput.value = amount.toFixed(2)
    }

    this.element.dispatchEvent(new Event("input", { bubbles: true }))
  }

  formatDisplay(amount) {
    const cents = Math.round(amount * 100) % 100
    const fractionDigits = cents === 0 ? 0 : 2

    try {
      return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      }).format(amount)
    } catch {
      return `$ ${amount.toFixed(fractionDigits).replace(".", ",")}`
    }
  }

  parseDecimal(raw) {
    if (raw === "" || raw == null) return null
    if (typeof raw === "number" && Number.isFinite(raw)) return raw

    const s = String(raw).trim().replace(/\s/g, "").replace(/^\$/, "")
    if (s === "") return null

    if (/^\d+(,\d+)?$/.test(s)) return parseFloat(s.replace(",", "."))
    if (/^\d+(\.\d+)?$/.test(s)) return parseFloat(s)

    const normalized = s.replace(/\./g, "").replace(",", ".")
    const n = parseFloat(normalized)
    return Number.isFinite(n) ? n : null
  }
}
